import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMspCreateTenant } from "@/hooks/use-api";
import { PLAN_CATALOG } from "@/lib/plan-catalog";
import type { MspCreateTenantResponse } from "@/types/api";

interface AddTenantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 59);
}

export function AddTenantDialog({ open, onOpenChange }: AddTenantDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [plan, setPlan] = useState("starter");
  const [result, setResult] = useState<MspCreateTenantResponse | null>(null);

  const createMutation = useMspCreateTenant();

  const handleNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(
      { name: name.trim(), slug, admin_email: adminEmail.trim(), plan },
      {
        onSuccess: (data) => setResult(data),
      },
    );
  };

  const handleClose = () => {
    if (!createMutation.isPending) {
      setName("");
      setSlug("");
      setSlugTouched(false);
      setAdminEmail("");
      setPlan("starter");
      setResult(null);
      createMutation.reset();
      onOpenChange(false);
    }
  };

  const isValid =
    name.trim().length > 0 &&
    slug.length >= 3 &&
    /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug) &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(adminEmail.trim());

  // --- Success screen ---
  if (result) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Tenant Created</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3">
              <p className="text-sm font-medium text-green-400">
                {result.tenant.name} has been provisioned successfully.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                A welcome email with sign-in instructions and a temporary password
                has been sent to {result.admin_user.email}.
              </p>
            </div>

            <div className="rounded-lg border bg-muted/50 p-4 space-y-2 text-sm">
              <p className="font-medium">Next steps</p>
              <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                <li>
                  The client admin signs in with the temporary password from the
                  welcome email and sets a new password.
                </li>
                <li>
                  Deploy the monitoring agent and connect integrations from the
                  client&apos;s setup page. The agent key{" "}
                  <code className="rounded bg-background px-1 py-0.5 font-mono text-xs">
                    {result.agent_key.key_prefix}…
                  </code>{" "}
                  can be retrieved in full once under{" "}
                  <strong>Settings &gt; Agents</strong> in the client tenant.
                </li>
              </ul>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  window.open(
                    result.integrations_url,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Open client setup
              </Button>
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Form screen ---
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Client</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tenant-name">Company Name</Label>
            <Input
              id="tenant-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Acme GmbH"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-slug">Slug</Label>
            <Input
              id="tenant-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
              }}
              placeholder="acme-gmbh"
              required
            />
            <p className="text-xs text-muted-foreground">
              3-63 lowercase alphanumeric characters or hyphens
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-email">Admin Email</Label>
            <Input
              id="admin-email"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@acme.de"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tenant-plan">Plan</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger id="tenant-plan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLAN_CATALOG.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {createMutation.isError && (
            <div id="add-tenant-form-error" role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {(createMutation.error as { message?: string })?.message ??
                "Failed to create tenant. Please try again."}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isValid || createMutation.isPending}
              aria-describedby={createMutation.isError ? "add-tenant-form-error" : undefined}
            >
              {createMutation.isPending ? "Creating..." : "Create Tenant"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
